import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import QRCodeStyling from 'qr-code-styling';
import env from 'react-dotenv';
import {
    chargerFile, chargerPubs, demanderJeton, effacerCle, envoyerSignal, extraireCle, extraireToken,
    lireCle, enregistrerCle, pairingComplete, pairingInit, parseResult,
} from './ecranApi';
import { couperVoix, lireAppel } from './voix';
import Logo from '../Assets/EBCallLogo.png';
import CarouselCustom from '../Components/CarousselCustom';
import { IconButton } from '../Components/ui';
import { getDominantColor, shadeHex } from '../Functions/Color/adaptiveColor';
import {
    Loader2, Megaphone, MonitorX,
    LayoutGrid, Rows, PanelRight, ArrowLeftRight,
    Palette, RotateCcw, Sun, Moon, X, VolumeX,
} from 'lucide-react';

/**
 * LiveScreemEcran — application TV autonome (aucune interaction, aucun login).
 *
 * Machine à états : ENROLEMENT → CONNEXION → AFFICHAGE.
 *  - ENROLEMENT : code affiché + poll PairingComplete jusqu'à la clé.
 *  - CONNEXION  : Jeton {CleEcran} → AFFICHAGE ; 401 → ENROLEMENT ; réseau → retry 30 s.
 *  - AFFICHAGE  : File + Signal/60 s + hub JoinEcran ; 401 → ENROLEMENT.
 */

type Etape = 'ENROLEMENT' | 'CONNEXION' | 'AFFICHAGE';
type HubEtat = 'connected' | 'reconnecting' | 'closed' | 'off';

const SECONDS = 1000;
const RETRY_CONNEXION = 30 * SECONDS;
const SIGNAL_EVERY = 60 * SECONDS;
const FILE_FALLBACK = 60 * SECONDS;
const FEATURE_MS = 10000;
const SEEN_MAX = 500;
const DOUBLON_MS = 8000;   // renvoi d'un appel terminé considéré comme doublon (reconnexion)

/** Numéro qui « vole » du centre de l'écran vers sa carte dans la grille (FLIP). */
function TicketVolant({ fly, onDone }: {
    fly: { numero: string; box?: string; from: DOMRect; to: DOMRect };
    onDone: () => void;
}) {
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const el = ref.current
        if (!el) { onDone(); return }
        const dx = (fly.to.left + fly.to.width / 2) - (fly.from.left + fly.from.width / 2)
        const dy = (fly.to.top + fly.to.height / 2) - (fly.from.top + fly.from.height / 2)
        const scale = Math.min(1, Math.max(0.15, fly.to.width / fly.from.width))
        const anim = el.animate(
            [
                { transform: 'translate(0px, 0px) scale(1)', opacity: 1 },
                { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 1 },
            ],
            { duration: 720, easing: 'cubic-bezier(.5, .05, .3, 1)', fill: 'forwards' },
        )
        anim.onfinish = onDone
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    return (
        <div ref={ref} className="ebc-fly"
            style={{ left: fly.from.left, top: fly.from.top, width: fly.from.width, height: fly.from.height }}>
            <span>{fly.numero}</span>
        </div>
    )
}

/** QR sombre sur pastille claire — scannable à distance. */
const CodeQR = ({ value }: { value: string }) => {
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!ref.current) return
        ref.current.innerHTML = ''
        new QRCodeStyling({
            width: 150, height: 150, data: value, margin: 0,
            qrOptions: { errorCorrectionLevel: 'M' },
            dotsOptions: { color: '#1c2340', type: 'rounded' },
            cornersSquareOptions: { color: '#1c2340', type: 'extra-rounded' },
            cornersDotOptions: { color: '#1c2340', type: 'dot' },
            backgroundOptions: { color: '#ffffff' },
        }).append(ref.current)
    }, [value])
    return <div ref={ref} className="rounded-xl bg-white p-2" />
};

/**
 * Médias pub renvoyés par File (Result ou Result.Affichage).
 * Tolerant : tableaux, objets {Fichier/FIchiers/src/Url/...}, JSON string, dataURL.
 */
/**
 * Médias de GET /api/Ecran/Pubs : Result.Publicites[].Fichiers est un TABLEAU
 * de data URL (« data:image/… », « data:video/… ») — pas de JSON.parse.
 */
function extrairePubs(result: any): { type: 'image' | 'video'; src: string }[] {
    const r = parseResult(result)
    const liste = r?.Publicites || r?.publicites || []
    const srcs: string[] = []
    liste.forEach((p: any) => {
        const fichiers = parseResult(p?.Fichiers ?? p?.fichiers)
        if (Array.isArray(fichiers)) fichiers.forEach((f: any) => { if (typeof f === 'string' && f) srcs.push(f) })
        else if (typeof fichiers === 'string' && fichiers) srcs.push(fichiers)
    })
    /** base64 brut → dataURL (signature fichier) ; dataURL/URL inchangées. */
    const normalizeSrc = (s: string) => {
        if (s.startsWith('data:') || /^https?:/i.test(s) || s.startsWith('/')) return s
        if (s.startsWith('/9j/')) return `data:image/jpeg;base64,${s}`
        if (s.startsWith('iVBOR')) return `data:image/png;base64,${s}`
        if (s.startsWith('R0lGOD')) return `data:image/gif;base64,${s}`
        if (s.startsWith('UklGR')) return `data:image/webp;base64,${s}`
        if (/^AAA[A-P].{0,4}ZnR5c/.test(s)) return `data:video/mp4;base64,${s}`   // box « ftyp »
        if (s.startsWith('GkXf')) return `data:video/webm;base64,${s}`
        return s
    }
    return srcs
        .filter((s) => typeof s === 'string' && s.length > 8)
        .map(normalizeSrc)
        .map((src) => {
            const isVideo = src.startsWith('data:')
                ? (src.split(';')[0].split(':')[1] || '').startsWith('video/')
                : /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(src)
            return { type: (isVideo ? 'video' : 'image') as 'image' | 'video', src }
        })
}

/** Presets bicolores — mêmes que LiveScreenPro. */
const PRESETS: [string, string][] = [
    ['#6366F1', '#4338CA'],
    ['#10B981', '#047857'],
    ['#F43F5E', '#BE123C'],
    ['#F59E0B', '#B45309'],
    ['#06B6D4', '#0369A1'],
    ['#A855F7', '#7E22CE'],
]

export default function LiveScreemEcran() {
    const [searchParams] = useSearchParams()
    const versionInstall = searchParams.get('v') || ''

    const [etape, setEtape] = useState<Etape>('ENROLEMENT')
    const [heure, setHeure] = useState('')
    const [hubEtat, setHubEtat] = useState<HubEtat>('off')

    // Enrôlement
    const [pairCode, setPairCode] = useState('')
    const [pairExpire, setPairExpire] = useState<Date | null>(null)
    const [erreurPairing, setErreurPairing] = useState('')

    // Affichage
    const [fileData, setFileData] = useState<any>(null)
    const [featured, setFeatured] = useState<{ codeS: string; numero: string; box?: string; msg?: string } | null>(null)
    // Vol du ticket : plein écran → sa carte dans la grille (fin d'appel)
    const [fly, setFly] = useState<{ numero: string; box?: string; from: DOMRect; to: DOMRect } | null>(null)
    const [landed, setLanded] = useState<string | null>(null)      // carte réceptrice à flasher
    const [callOut, setCallOut] = useState(false)                  // fondu de sortie de l'overlay
    const featuredRef = useRef<typeof featured>(null)

    /* ---------- Présentation (identique LiveScreenPro) ---------- */
    const [layout, setLayout] = useState<'split' | 'board' | 'pub'>('split')
    const [swapped, setSwapped] = useState(false)
    const [showControls, setShowControls] = useState(false)
    const [paletteOpen, setPaletteOpen] = useState(false)
    const [pubColor, setPubColor] = useState<string | null>(null)
    const [pub, setPub] = useState<{ type: 'image' | 'video'; src: string }[]>([])

    // Personnalisation des couleurs (prioritaire sur celles du tenant)
    const [accA, setAccA] = useState<string | null>(() => {
        try { return JSON.parse(localStorage.getItem('eb-lsp-colors') || '[]')[0] || null } catch { return null }
    })
    const [accB, setAccB] = useState<string | null>(() => {
        try { return JSON.parse(localStorage.getItem('eb-lsp-colors') || '[]')[1] || null } catch { return null }
    })

    // Thème de l'écran : 'dark' | 'light' (persisté)
    const [mode, setMode] = useState<'dark' | 'light'>(() =>
        (localStorage.getItem('eb-lsp-mode') as 'dark' | 'light') || 'dark')
    function applyMode(m: 'dark' | 'light') {
        setMode(m)
        localStorage.setItem('eb-lsp-mode', m)
    }
    function applyColors(a: string | null, b: string | null) {
        setAccA(a); setAccB(b)
        if (a || b) localStorage.setItem('eb-lsp-colors', JSON.stringify([a, b]))
        else localStorage.removeItem('eb-lsp-colors')
    }

    function onPubSlideChange(index: number) {
        const item = pub[index]
        if (item?.type === 'image') getDominantColor(item.src).then(setPubColor)
        else setPubColor(null)
    }

    /* ---------- refs (jeton en MÉMOIRE seulement) ---------- */
    const jeton = useRef<string | null>(null)
    const contexte = useRef<{ codeFiliale?: string; services: string[] }>({ services: [] })
    const fileRef = useRef<any>(null)
    // Dernière liste de pubs reçue — continue de tourner si le réseau tombe
    const pubsRef = useRef<{ type: 'image' | 'video'; src: string }[]>([])
    const hub = useRef<signalR.HubConnection | null>(null)
    const timers = useRef<{
        pair?: any; connexion?: any; jeton?: any; signal?: any;
        file?: any; hubRetry?: any; feat?: any; featEn?: any; expire?: any;
    }>({})
    const seenEvents = useRef<Map<string, number>>(new Map())
    const appelCourant = useRef<string | null>(null)             // appelId affiché / lu (AppelEcran)
    const mounted = useRef(true)
    // Générations anti-course : une seule chaîne enrôlement/connexion active à la fois
    const enrolGen = useRef(0)
    const connGen = useRef(0)
    const affichageOn = useRef(false)

    const clearTimer = (k: keyof typeof timers.current) => {
        if (timers.current[k]) { clearTimeout(timers.current[k]); clearInterval(timers.current[k]); timers.current[k] = undefined }
    }

    /* ================= ENROLEMENT ================= */

    const demarrerEnrolement = useCallback(async () => {
        const gen = ++enrolGen.current
        clearTimer('pair'); clearTimer('expire')
        setErreurPairing('')
        const r = await pairingInit()
        if (!mounted.current || gen !== enrolGen.current) return
        if (r.statut !== 200 || !r.result?.PairingCode) {
            setErreurPairing(r.message || 'Impossible de générer le code — nouvelle tentative…')
            timers.current.pair = setTimeout(() => { if (gen === enrolGen.current) demarrerEnrolement() }, RETRY_CONNEXION)
            return
        }
        const code = r.result.PairingCode
        setPairCode(code)
        const expire = r.result.PairingExpire ? new Date(r.result.PairingExpire) : null
        setPairExpire(expire)
        if (expire) {
            timers.current.expire = setTimeout(() => { if (gen === enrolGen.current) demarrerEnrolement() }, Math.max(1000, expire.getTime() - Date.now()))
        }
        const intervalle = Math.max(2, Number(r.result.IntervalleInterrogationSec) || 5) * SECONDS
        const pairInterval = setInterval(async () => {
            if (!mounted.current || gen !== enrolGen.current) { clearInterval(pairInterval); return }
            const c = await pairingComplete(code)
            if (!mounted.current || gen !== enrolGen.current) return
            if (c.statut === 202 || c.http === 202) return       // en attente de validation
            const cle = c.statut === 200 ? extraireCle(c.result) : null
            if (cle) {
                clearTimer('pair'); clearTimer('expire')
                enregistrerCle(cle)
                setEtape('CONNEXION')
                return
            }
            if (c.statut === 404 || c.http === 404) {
                clearTimer('pair'); clearTimer('expire')
                demarrerEnrolement()                            // code périmé → nouveau code
            }
        }, intervalle)
        timers.current.pair = pairInterval
    }, [])

    /* ================= CONNEXION ================= */

    const connecter = useCallback(async (silencieux = false) => {
        const gen = ++connGen.current
        const cle = lireCle()
        if (!cle) { setEtape('ENROLEMENT'); return }
        const r = await demanderJeton(cle)
        if (!mounted.current || gen !== connGen.current) return
        const result = parseResult(r.result)
        const token = r.statut === 200 ? extraireToken(result) : null
        if (token) {
            jeton.current = token
            contexte.current = {
                codeFiliale: result.CodeFiliale,
                services: (result.Services || []).map((s: any) => s.CodeS || s).filter(Boolean),
            }
            setEtape('AFFICHAGE')
            planifierRenouvellement(result)
            // Renouvellement silencieux : ne PAS reconstruire hub + intervalles
            if (!affichageOn.current) demarrerAffichage()
            return
        }
        if (r.statut === 401 || r.http === 401) {
            effacerCle(); jeton.current = null
            setEtape('ENROLEMENT')
            return
        }
        // Réseau : réessayer (silencieux = sans quitter l'affichage)
        if (!silencieux) setEtape('CONNEXION')
        timers.current.connexion = setTimeout(() => { if (gen === connGen.current) connecter(silencieux) }, RETRY_CONNEXION)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Renouvellement à TokenExpireLe - RenouvelerAvantSec. */
    function planifierRenouvellement(result: any) {
        clearTimer('jeton')
        const expire = result?.TokenExpireLe ? new Date(result.TokenExpireLe).getTime() : 0
        const avant = (Number(result?.RenouvelerAvantSec) || 60) * SECONDS
        const delai = expire ? Math.max(5 * SECONDS, expire - avant - Date.now()) : 5 * 60 * SECONDS
        timers.current.jeton = setTimeout(() => connecter(true), delai)
    }

    /* ================= AFFICHAGE ================= */

    const loadFile = useCallback(async () => {
        if (!jeton.current) return
        const r = await chargerFile(jeton.current)
        if (!mounted.current) return
        if (r.statut === 401 || r.http === 401) { revoquer(); return }
        if (r.statut === 200 && r.result) {
            fileRef.current = r.result
            setFileData(r.result)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * Publicités — GET /api/Ecran/Pubs (jeton écran). Appelée au démarrage,
     * après JoinEcran/(re)connexion et sur PubsUpdated — JAMAIS en boucle
     * (réponse de plusieurs Mo). En cas d'échec réseau la dernière liste
     * reçue continue de tourner.
     */
    const loadPubs = useCallback(async () => {
        if (!jeton.current) return
        const r = await chargerPubs(jeton.current)
        if (!mounted.current) return
        if (r.statut === 401 || r.http === 401) { revoquer(); return }
        if (r.statut === 200) {
            pubsRef.current = extrairePubs(r.result)
            setPub(pubsRef.current)
        }
        // statut 0 (réseau) : garder pubsRef — la diffusion continue
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Fin d'appel : fondu de l'overlay + le numéro vole vers sa carte (FLIP). */
    function cloreAppel() {
        const f = featuredRef.current
        if (!f) return
        const numEl = document.getElementById('ebc-call-num')
        const cell = document.querySelector(`[data-ebc-cell="${CSS.escape(f.numero)}"]`)
        if (numEl && cell) {
            setFly({ numero: f.numero, box: f.box, from: numEl.getBoundingClientRect(), to: cell.getBoundingClientRect() })
        }
        setCallOut(true)
        timers.current.feat = setTimeout(() => { setFeatured(null); setCallOut(false) }, 320)
    }

    function revoquer() {
        effacerCle(); jeton.current = null; fileRef.current = null
        connGen.current++                          // tue toute chaîne de connexion en cours
        stopAffichage()
        setEtape('ENROLEMENT')
    }

    function stopAffichage() {
        affichageOn.current = false
        appelCourant.current = null
        couperVoix()
        ;(['signal', 'file', 'hubRetry', 'feat', 'featEn', 'connexion', 'jeton'] as const).forEach(clearTimer)
        if (hub.current) { hub.current.stop().catch(() => { }); hub.current = null }
    }

    function demarrerAffichage() {
        affichageOn.current = true
        loadFile()
        loadPubs()
        // Signal de vie
        clearTimer('signal')
        timers.current.signal = setInterval(async () => {
            if (!jeton.current) return
            const r = await envoyerSignal(jeton.current, versionInstall)
            if (!mounted.current) return
            if (r.statut === 401 || r.http === 401) { revoquer(); return }
            if (r.result?.Commande === 'RECHARGER') window.location.reload()
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, SIGNAL_EVERY)
        // Filet de sécurité : rechargement de la file toutes les 60 s
        clearTimer('file')
        timers.current.file = setInterval(loadFile, FILE_FALLBACK)
        demarrerHub()
    }

    /* ---------- SignalR /hubs/queue — JoinEcran ---------- */

    /** Horodate la fin d'un appel (anti-doublon court, voir DOUBLON_MS) et purge les anciennes entrées. */
    function marquerFini(appelId: string) {
        const now = Date.now()
        seenEvents.current.set(`T|${appelId}`, now)
        if (seenEvents.current.size > SEEN_MAX) {
            seenEvents.current.forEach((ts, k) => {
                if (now - ts > DOUBLON_MS) seenEvents.current.delete(k)
            })
        }
    }

    function vitesseService(codeS?: string): number {
        const svc = (fileRef.current?.Services || []).find((s: any) => s.CodeS === codeS)
        return Number(svc?.VitesseAppel) || 1
    }

    function demarrerHub() {
        if (hub.current) { hub.current.stop().catch(() => { }); hub.current = null }
        clearTimer('hubRetry')

        const conn = new signalR.HubConnectionBuilder()
            .withUrl(env.API_BASE_URL + 'hubs/queue', {
                accessTokenFactory: () => jeton.current || '',
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .build()
        hub.current = conn

        const join = () => conn.invoke('JoinEcran')
            .then(() => { loadFile(); loadPubs() })
            .catch(() => { })

        conn.onreconnecting(() => setHubEtat('reconnecting'))
        conn.onreconnected(async () => { setHubEtat('connected'); await join() })
        conn.onclose(() => {
            setHubEtat('closed')
            // Renouveler le jeton puis retenter toutes les 30 s
            clearTimer('hubRetry')
            timers.current.hubRetry = setInterval(async () => {
                try {
                    await demanderJeton(lireCle() || '').then((r) => {
                        const t = r.statut === 200 ? extraireToken(r.result) : null
                        if (t) jeton.current = t
                    })
                    await conn.start(); setHubEtat('connected'); await join()
                    clearTimer('hubRetry')
                } catch { /* retente au prochain cycle */ }
            }, RETRY_CONNEXION)
        })

        /* ----- Événements ----- */

        /*
         * AppelEcran — le serveur tient la file d'appels de CET écran et n'envoie
         * qu'un appel à la fois. SEUL endroit qui change le grand affichage.
         * Voix lue jusqu'au bout puis AppelTermine(appelId) → le serveur envoie le suivant.
         * jouerVoix = false : afficher seulement (le serveur tient la durée).
         */
        conn.on('AppelEcran', async (p: any) => {
            const appelId = String(p?.appelId ?? '')
            const numero = String(p?.numeroTicket || p?.codeT || '')
            const box = p?.numBox != null && p?.numBox !== '' ? String(p.numBox) : undefined
            const codeS = p?.codeS || ''
            const jouer = p?.jouerVoix === true
            const annonces: any[] = (p?.annonces || [])
                .slice()
                .sort((a: any, b: any) => (Number(a?.priorite) || 1) - (Number(b?.priorite) || 1))

            if (process.env.NODE_ENV !== 'production') console.info('[AppelEcran]', appelId, numero, box, codeS, { jouer })

            // Doublon RÉCENT d'un appel terminé (renvoi après reconnexion) : ré-accuser sans rejouer.
            // Fenêtre courte : un rappel volontaire du même ticket plus tard doit être rejoué,
            // même si le serveur réutilise le même appelId.
            const finiA = appelId ? seenEvents.current.get(`T|${appelId}`) : undefined
            if (finiA !== undefined && Date.now() - finiA < DOUBLON_MS) {
                if (jouer) conn.invoke('AppelTermine', appelId).catch(() => { })
                return
            }
            // Doublon de l'appel EN COURS (encore affiché / lu) : ignorer
            if (appelId && appelId === appelCourant.current) return
            if (appelId) seenEvents.current.delete(`T|${appelId}`)
            appelCourant.current = appelId

            couperVoix()                                         // jamais deux appels à la fois
            clearTimer('feat'); clearTimer('featEn')
            setCallOut(false)
            setFeatured({ codeS, numero, box, msg: annonces[0]?.texte || numero })

            if (!jouer) {
                // Muet : affichage seul, le serveur enverra le suivant après sa durée
                const ms = Number(p?.dureeAffichageSec) > 0 ? Number(p.dureeAffichageSec) * SECONDS : FEATURE_MS
                timers.current.feat = setTimeout(() => {
                    if (appelCourant.current === appelId) { appelCourant.current = null; cloreAppel() }
                }, ms)
                return
            }

            // Message affiché = phrase en cours de lecture
            const fini = await lireAppel(annonces, vitesseService(codeS), (_i, texte) => {
                if (appelCourant.current === appelId) setFeatured((f) => (f && f.numero === numero ? { ...f, msg: texte } : f))
            })
            if (!fini || appelCourant.current !== appelId) return     // remplacé entre-temps
            if (appelId) marquerFini(appelId)
            appelCourant.current = null                          // l'appel n'est plus « en cours »
            conn.invoke('AppelTermine', appelId).catch(() => { })
            // Le serveur envoie le suivant ~1 s après ; sinon on rend la main aux tuiles
            timers.current.feat = setTimeout(() => {
                if (appelCourant.current === null) cloreAppel()
            }, 2500)
        })

        // TicketCalled : plus utilisé pour la voix ni le grand affichage — tuiles seulement
        conn.on('TicketCalled', () => loadFile())

        conn.on('QueueSnapshot', (p: any) => {
            // Mise à jour directe si le payload porte le service + les numéros
            const codeS = p?.codeS || p?.CodeS
            const guichets = p?.guichets || p?.Guichets
            const prochains = p?.prochains || p?.Prochains
            if (!codeS) { loadFile(); return }
            const next = { ...(fileRef.current || {}), Services: (fileRef.current?.Services || []).map((s: any) =>
                s.CodeS === codeS
                    ? { ...s, ...(guichets ? { Guichets: guichets } : {}), ...(prochains ? { Prochains: prochains } : {}) }
                    : s) }
            fileRef.current = next
            setFileData(next)
        })

        const refreshEvents = ['FileUpdated', 'TicketValidated', 'TicketRejected',
            'TicketTransferred', 'TicketNoShow', 'TicketCancelled']
        refreshEvents.forEach(ev => conn.on(ev, () => loadFile()))

        // Nouvelle liste de pubs publiée → rechargement ponctuel (jamais en boucle)
        conn.on('PubsUpdated', () => loadPubs())

        // Commandes back office
        conn.on('EcranCommande', (p: any) => {
            const cmd = p?.commande || p?.Commande
            if (cmd === 'RECHARGER') window.location.reload()
            else if (cmd === 'REVOQUE') revoquer()
        })

        conn.start()
            .then(async () => { setHubEtat('connected'); await join() })
            .catch(() => {
                setHubEtat('closed')
                clearTimer('hubRetry')
                timers.current.hubRetry = setInterval(() => demarrerHub(), RETRY_CONNEXION)
            })
    }

    /* ================= BOOT ================= */

    useEffect(() => {
        mounted.current = true
        const clock = setInterval(() => setHeure(new Date().toLocaleTimeString('fr-FR')), 1000)
        if (lireCle()) setEtape('CONNEXION')
        return () => {
            mounted.current = false
            enrolGen.current++; connGen.current++   // tue les continuations async orphelines
            clearInterval(clock)
            Object.keys(timers.current).forEach(k => clearTimer(k as any))
            if (hub.current) hub.current.stop().catch(() => { })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Point d'entrée unique des transitions : ENROLEMENT et CONNEXION
    useEffect(() => {
        if (etape === 'CONNEXION') connecter()
        else if (etape === 'ENROLEMENT') {
            if (lireCle()) { setEtape('CONNEXION'); return }
            setPairCode('')
            demarrerEnrolement()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [etape])

    // Les pubs ne viennent plus de File — elles arrivent par Ecran/Pubs
    // (chargement au démarrage + PubsUpdated, voir loadPubs).

    // featured lu depuis les timers (cloreAppel)
    useEffect(() => { featuredRef.current = featured }, [featured])

    /* ---------- Densité adaptative : tout doit tenir SANS scroll (TV) ---------- */
    const boardRef = useRef<HTMLElement | null>(null)
    const [boardSize, setBoardSize] = useState({ w: 0, h: 0 })
    useEffect(() => {
        const el = boardRef.current
        if (!el) return
        const maj = () => setBoardSize({ w: el.clientWidth, h: el.clientHeight })
        maj()
        const ro = new ResizeObserver(maj)
        ro.observe(el)
        return () => ro.disconnect()
    }, [etape, layout])

    /* ================= RENDU ================= */

    const aff = fileData?.Affichage || {}
    const [c1, c2] = String(aff.COLORS || '#4338CA|#3730A3').split('|')
    const services: any[] = fileData?.Services || []

    // Priorité : couleur dominante de la pub > personnalisation > tenant.
    const pubVisible = layout !== 'board'
    const accent = (pubVisible && pubColor) || accA || c1 || '#4338CA'
    const accent2 = (pubVisible && pubColor ? shadeHex(pubColor, -35) : null) || accB || c2 || '#3730A3'

    /* --------------------- Panneau tableau (file) --------------------- */
    // Estimation des hauteurs (px) — réduites par `densite` si tout ne tient pas
    const isFullBoard = layout === 'board'
    const nextBase = isFullBoard ? 120 : 100
    // En-tête de service (+ marge de séparation quand les services sont empilés)
    const svcHeadBase = isFullBoard ? 64 : 50 + 44

    /* ----- Disposition adaptative : les services en colonnes en vue plein écran ----- */
    const svcCols = isFullBoard && services.length > 1 ? Math.min(services.length, 3) : 1
    const gapSvc = svcCols > 1 ? 44 : 0
    // Largeur disponible par colonne de service (fallback si pas encore mesuré)
    const largeurSvc = boardSize.w > 0
        ? (boardSize.w - gapSvc * (svcCols - 1) - 40) / svcCols
        : (isFullBoard ? 1400 : 680)

    const cellBase = isFullBoard ? 150 : 115
    // Vue partagée : une seule colonne de barres (pleine largeur) tant que la hauteur le permet
    const hauteurPour = (cols: number) => 16 + services.reduce((n, s) =>
        n + Math.ceil(Math.max((s.Guichets || []).length, 1) / cols) * cellBase + nextBase +
        (services.length > 1 ? svcHeadBase : 0), 0)
    const splitUneColonne = !isFullBoard && boardSize.h > 0 && hauteurPour(1) * 0.75 <= boardSize.h

    /** Colonnes de barres : assez pour remplir, assez peu pour rester lisible. */
    const colsCartes = (n: number) => {
        if (splitUneColonne) return 1
        const ideal = n <= 1 ? 1 : n <= 4 ? 2 : n <= 6 ? 3 : n <= 12 ? 4 : n <= 20 ? 5 : 6
        const maxParLargeur = Math.max(1, Math.floor(largeurSvc / (isFullBoard ? 260 : 340)))
        return Math.min(ideal, maxParLargeur)
    }
    const lignesSvc = (s: any) =>
        Math.ceil(Math.max((s.Guichets || []).length, 1) / colsCartes((s.Guichets || []).length)) * cellBase

    const needed = services.length === 0
        ? cellBase + 40
        : 16 + services.reduce((n, s) =>
            n + lignesSvc(s) + nextBase +
            (services.length > 1 ? svcHeadBase : 0), 0)
    // < 1 : on réduit pour tout faire tenir ; vue partagée : on agrandit pour occuper toute la hauteur
    const ratioH = boardSize.h > 0 ? boardSize.h / needed : 1
    const densite = ratioH < 1 ? Math.max(0.4, ratioH) : (isFullBoard ? 1 : Math.min(2.4, ratioH))

    // Couleurs des cartes selon le thème (texte foncé sur fond clair)
    const clair = mode === 'light'
    const tk = clair ? {
        num: '#0f172a', off: 'rgba(15,23,42,.28)', line: 'rgba(15,23,42,.10)',
        box: 'color-mix(in srgb, var(--lsp-a) 80%, #0f172a)', cardOn: 'rgba(255,255,255,.85)', cardOff: 'rgba(15,23,42,.04)',
    } : {
        num: '#fff', off: 'rgba(148,163,184,.4)', line: 'rgba(255,255,255,.10)',
        box: 'color-mix(in srgb, var(--lsp-a) 85%, #fff)', cardOn: 'rgba(255,255,255,.05)', cardOff: 'rgba(255,255,255,.04)',
    }

    const boardPanel = (
        <aside ref={boardRef as any} className={`eb-lsp-board${isFullBoard ? ' is-full' : ''}`}
            style={{ overflow: 'hidden' }}>
            {services.length === 0 && (
                <div className="eb-lsp-rows">
                    <div className="eb-lsp-rows-head">
                        <span>Box</span>
                        <span>Client</span>
                    </div>
                    <div className="eb-lsp-row is-empty"><span>—</span><span>…</span></div>
                </div>
            )}

            {/* En vue plein écran multi-services : les services en colonnes (grille) */}
            <div className="flex flex-col min-h-0 flex-1"
                style={svcCols > 1 ? {
                    display: 'grid',
                    gridTemplateColumns: `repeat(${svcCols}, minmax(0, 1fr))`,
                    gap: gapSvc,
                    flex: 1,
                } : undefined}>
            {services.map((s: any, si: number) => {
                const guichets: any[] = s.Guichets || []
                const prochains: any[] = s.Prochains || []
                return (
                    <div key={s.CodeS || si} className="flex flex-col min-h-0"
                        style={si > 0 && svcCols === 1 ? {
                            // Marge nette + séparateur entre deux services empilés
                            marginTop: 22 * densite, paddingTop: 20 * densite,
                            borderTop: `2px solid ${tk.line}`,
                        } : undefined}>
                        {services.length > 1 && (
                            <div className="eb-lsp-rows-head" style={{
                                borderBottom: `2px solid color-mix(in srgb, var(--lsp-a) 40%, transparent)`,
                                paddingBottom: `${8 * densite}px`,
                                marginBottom: `${10 * densite}px`,
                                fontSize: `${(isFullBoard ? 1.6 : 1.2) * densite}rem`,
                                letterSpacing: '.06em',
                            }}>
                                <span className="flex items-center font-extrabold uppercase" style={{ gap: `${12 * densite}px`, color: 'var(--lsp-a)' }}>
                                    <Megaphone size={(isFullBoard ? 26 : 20) * densite} />
                                    {s.Libelle || s.CodeS}
                                </span>
                            </div>
                        )}
                        {/* Guichets — grille de grosses cartes « ticket → box » (adaptée au nombre, jamais de scroll) */}
                        <div className="grid min-h-0 flex-1 content-stretch" style={{
                            overflow: 'hidden',
                            gap: `${(isFullBoard ? 18 : 14) * densite}px`,
                            gridTemplateColumns: `repeat(${colsCartes(Math.max(guichets.length, 1))}, minmax(0, 1fr))`,
                        }}>
                            {guichets.length === 0 && (
                                <div className="eb-lsp-row is-empty" style={{ padding: `${12 * densite}px 14px` }}><span>—</span><span>…</span></div>
                            )}
                            {guichets.map((g: any, i: number) => {
                                const on = !!g.NumeroTicket
                                const isNew = on && (featured?.numero === g.NumeroTicket || landed === g.NumeroTicket)
                                const nom = [s.Libelle, g.NumBox].filter((x: any) => x != null && x !== '').join(' ')
                                // Plein écran : tailles ajustées à la largeur réelle de la carte (jamais de débordement)
                                // Vue partagée : barre « ticket ··· guichet », tailles bornées par la largeur
                                const split = (() => {
                                    if (isFullBoard) return null
                                    const cols = colsCartes(Math.max(guichets.length, 1))
                                    const carteW = (largeurSvc - (cols - 1) * 14 * densite) / cols
                                    const inner = Math.max(80, carteW - 2 * 22 * densite - 4)
                                    let num = 3.4 * 16 * densite, box = 1.8 * 16 * densite
                                    const besoin = num * Math.max(3, String(g.NumeroTicket || '—').length) * 0.62 +
                                        box * Math.max(1, nom.length) * 0.8 + 26 * densite + 16
                                    if (besoin > inner) { const k = inner / besoin; num *= k; box *= k }
                                    return { num, box }
                                })()
                                const fit = (() => {
                                    if (!isFullBoard) return null
                                    const cols = colsCartes(Math.max(guichets.length, 1))
                                    const carteW = (largeurSvc - (cols - 1) * 18 * densite) / cols
                                    const inner = Math.max(60, carteW - 2 * 18 * densite - 4)
                                    const remPx = 16
                                    const num = Math.min(4.6 * remPx * densite, inner / (Math.max(3, String(g.NumeroTicket || '—').length) * 0.62))
                                    const base = 2.4 * remPx * densite
                                    const dot = 26 * densite
                                    const uneLigne = (inner - dot) / (Math.max(1, nom.length) * 0.8)
                                    if (uneLigne >= Math.min(base, 22)) return { num, nom: Math.min(base, uneLigne), wrap: false }
                                    // Trop long : passage sur 2 lignes, taille recalculée sur la moitié du libellé
                                    const deux = inner / (Math.ceil(nom.length / 2 + 1) * 0.8)
                                    return { num, nom: Math.max(12, Math.min(base, deux)), wrap: true }
                                })()
                                return (
                                    <div key={i}
                                        data-ebc-cell={on ? g.NumeroTicket : undefined}
                                        className={isFullBoard
                                            ? "flex flex-col items-center justify-center rounded-2xl"
                                            : "flex items-center justify-between rounded-2xl"}
                                        style={{
                                            gap: `${(isFullBoard ? 10 : 0) * densite}px`,
                                            overflow: 'hidden',
                                            padding: `${(isFullBoard ? 14 : 10) * densite}px ${(isFullBoard ? 18 : 22) * densite}px`,
                                            border: `1.5px solid ${isNew ? 'color-mix(in srgb, var(--lsp-a) 70%, transparent)' : on ? 'color-mix(in srgb, var(--lsp-a) 30%, transparent)' : tk.line}`,
                                            background: on
                                                ? `linear-gradient(160deg, color-mix(in srgb, var(--lsp-a) 14%, transparent), ${tk.cardOn})`
                                                : tk.cardOff,
                                            boxShadow: isNew ? '0 0 28px color-mix(in srgb, var(--lsp-a) 45%, transparent)' : undefined,
                                            animation: isNew ? 'eb-fade-up .4s ease both, eb-lsp-rowflash 1.8s ease-out both' : 'eb-fade-up .35s ease both',
                                        }}>
                                        {/* Numéro de ticket — très grand, à gauche */}
                                        <span className="tabular-nums font-black leading-none"
                                            style={{ fontSize: `${fit ? fit.num : split!.num}px`, color: on ? tk.num : tk.off, whiteSpace: 'nowrap' }}>
                                            {g.NumeroTicket || '—'}
                                        </span>
                                        {/* Guichet — sous le ticket en plein écran, à droite sinon */}
                                        {fit ? (
                                            <span className="flex items-center justify-center uppercase font-extrabold tabular-nums"
                                                style={{ fontSize: `${fit.nom}px`, letterSpacing: '.08em', gap: `${12 * densite}px`, color: on ? tk.box : tk.off, maxWidth: '100%', minWidth: 0 }}>
                                                <i className="eb-lsp-dot" style={{ flexShrink: 0, width: 12 * densite, height: 12 * densite, background: on ? 'var(--lsp-a)' : tk.off }} />
                                                <span style={fit.wrap ? {
                                                    minWidth: 0, textAlign: 'center', lineHeight: 1.1, overflowWrap: 'anywhere',
                                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                } : { minWidth: 0, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {nom}
                                                </span>
                                            </span>
                                        ) : (
                                            <span className="flex items-center uppercase font-extrabold tabular-nums leading-none whitespace-nowrap"
                                                style={{ fontSize: `${split!.box}px`, letterSpacing: '.08em', gap: `${split!.box * 0.45}px`, color: on ? tk.box : tk.off, minWidth: 0 }}>
                                                <i className="eb-lsp-dot" style={{ flexShrink: 0, width: split!.box * 0.4, height: split!.box * 0.4, background: on ? 'var(--lsp-a)' : tk.off }} />
                                                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{nom}</span>
                                            </span>
                                        )}
                                    </div>
                                )
                            })}
                        </div>

                        {/* Prochains appels — espace au-dessus du bloc */}
                        <div className="eb-lsp-next" style={{ padding: `${10 * densite}px ${16 * densite}px`, marginTop: `${(isFullBoard ? 18 : 12) * densite}px` }}>
                            <span className="eb-lsp-next-label" style={{ fontSize: `${(isFullBoard ? 0.95 : 0.8) * densite}rem` }}>Suivants</span>
                            <div className="eb-lsp-next-row" style={{ gap: `${10 * densite}px` }}>
                                {prochains.length === 0 && <span className="eb-lsp-next-none">—</span>}
                                {prochains.slice(0, 5).map((t: any, i: number) => (
                                    <span key={i} className="eb-lsp-next-chip"
                                        style={{ animationDelay: `${i * 70}ms`, fontSize: `${(isFullBoard ? 1.7 : 1.3) * densite}rem`, padding: `${(isFullBoard ? 12 : 8) * densite}px ${(isFullBoard ? 26 : 18) * densite}px`, borderColor: 'color-mix(in srgb, var(--lsp-a) 27%, transparent)', background: 'color-mix(in srgb, var(--lsp-a) 8%, transparent)' }}>
                                        {typeof t === 'object' ? (t.CodeT || t.NumeroTicket || t.numero || '') : t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            })}
            </div>
        </aside>
    )

    /* ------------------------- Panneau pub ------------------------- */
    const pubPanel = (
        <section className="eb-lsp-stage" style={{ width: layout === 'split' ? '62%' : '100%' }}>
            <div className="eb-lsp-stage-frame"
                style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--lsp-pub) 55%, #0d1220), color-mix(in srgb, var(--lsp-pub) 30%, #0d1220))' }}>
                {pub.length > 0 ? (
                    <CarouselCustom items={pub} onSlideChange={onPubSlideChange} />
                ) : (
                    <div className="eb-lsp-stage-empty">
                        <img width={220} src={Logo} alt="" />
                        <div className="eb-lsp-stage-loading">
                            <Loader2 size={26} className="animate-spin" />
                            <span>Chargement des publicités…</span>
                        </div>
                    </div>
                )}
            </div>
        </section>
    )

    const leftPanel = swapped ? boardPanel : pubPanel
    const rightPanel = swapped ? pubPanel : boardPanel

    return (
        <div
            className={`eb-lsp${mode === 'light' ? ' is-light' : ''}`}
            style={{
                // Variables enregistrées @property : les changements de couleur sont animés (fondu)
                ['--lsp-a' as any]: accent,
                ['--lsp-b' as any]: accent2,
                // Couleur dominante de la pub courante — teinte le cadre (letterbox ambiant)
                ['--lsp-pub' as any]: pubColor || '#0d1220',
                background:
                    `radial-gradient(130% 110% at 0% 0%, color-mix(in srgb, var(--lsp-a) ${mode === 'light' ? 25 : 20}%, transparent), transparent 55%),` +
                    `radial-gradient(130% 110% at 100% 100%, color-mix(in srgb, var(--lsp-b) ${mode === 'light' ? 24 : 18}%, transparent), transparent 55%),` +
                    `radial-gradient(90% 70% at 60% 45%, color-mix(in srgb, var(--lsp-a) ${mode === 'light' ? 12 : 8}%, transparent), transparent 60%),` +
                    (mode === 'light' ? '#eef1f7' : '#070a13'),
            }}
            onMouseMove={(e) => {
                if (e.clientY < 60) setShowControls(true)
                else if (e.clientY > 120) setShowControls(false)
            }}
        >
            <style>{`
                @keyframes ebc-call-in { 0% { opacity: 0; transform: scale(.82) } 55% { opacity: 1; transform: scale(1.04) } 100% { transform: scale(1) } }
                @keyframes ebc-call-pulse { 0%,100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--lsp-a) 55%, transparent) } 70% { box-shadow: 0 0 0 46px transparent } }
                @keyframes ebc-msg-in { from { opacity: 0; transform: translateY(18px) } to { opacity: 1; transform: none } }
                .ebc-call-overlay {
                    position: fixed; inset: 0; z-index: 60;
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                    gap: 3vh; padding: 6vh 6vw; text-align: center;
                    background:
                        radial-gradient(120% 120% at 50% 30%, color-mix(in srgb, var(--lsp-a) 30%, transparent), transparent 60%),
                        radial-gradient(120% 120% at 50% 100%, color-mix(in srgb, var(--lsp-b) 25%, transparent), transparent 60%),
                        rgba(7, 10, 19, .88);
                    backdrop-filter: blur(8px);
                    animation: ebc-call-in .55s cubic-bezier(.2, .9, .25, 1.15) both;
                }
                .ebc-call-badge {
                    display: inline-flex; align-items: center; gap: 12px; padding: .5em 1.4em;
                    border-radius: 999px; font-weight: 800; letter-spacing: .3em;
                    font-size: clamp(1rem, 2vw, 1.8rem); color: #fff;
                    background: color-mix(in srgb, var(--lsp-a) 35%, transparent);
                    border: 1.5px solid color-mix(in srgb, var(--lsp-a) 65%, transparent);
                }
                .ebc-call-num {
                    font-size: clamp(7rem, 24vw, 22rem); line-height: .95; font-weight: 900;
                    letter-spacing: .02em; color: #fff;
                    text-shadow: 0 0 60px color-mix(in srgb, var(--lsp-a) 60%, transparent);
                    animation: ebc-call-pulse 1.6s ease-in-out infinite;
                    border-radius: 40px; padding: 0 .15em;
                }
                .ebc-call-dest { font-size: clamp(1.6rem, 4vw, 3.4rem); font-weight: 800; color: #fff; }
                .ebc-call-svc {
                    font-size: clamp(1rem, 2vw, 1.6rem); letter-spacing: .2em; text-transform: uppercase;
                    color: color-mix(in srgb, var(--lsp-a) 75%, #fff); font-weight: 700;
                }
                .ebc-call-msg {
                    font-size: clamp(1.2rem, 2.6vw, 2.2rem); font-weight: 600;
                    color: rgba(255,255,255,.85); max-width: 80vw;
                    animation: ebc-msg-in .5s ease both;
                }
                .ebc-call-overlay.is-out {
                    animation: none;
                    opacity: 0;
                    transition: opacity .3s ease;
                }
                .ebc-fly {
                    position: fixed; z-index: 70;
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                    pointer-events: none; color: #fff; will-change: transform;
                }
                .ebc-fly span {
                    font-size: clamp(6rem, 22vw, 20rem); line-height: 1; font-weight: 900;
                    text-shadow: 0 0 50px color-mix(in srgb, var(--lsp-a) 60%, transparent);
                }
                .ebc-fly small { font-size: clamp(1.2rem, 3vw, 2.4rem); font-weight: 800; }
                .eb-lsp.is-light .ebc-fly { color: #0f172a; }
                .eb-lsp.is-light .ebc-fly span { text-shadow: 0 0 40px color-mix(in srgb, var(--lsp-a) 35%, transparent); }
            `}</style>

            {/* Halos colorés tenant */}
            <span className="eb-lsp-glow g1" style={{ background: 'radial-gradient(closest-side, color-mix(in srgb, var(--lsp-a) 30%, transparent), transparent 72%)' }} />
            <span className="eb-lsp-glow g2" style={{ background: 'radial-gradient(closest-side, color-mix(in srgb, var(--lsp-b) 26%, transparent), transparent 72%)' }} />
            <span className="eb-lsp-glow g3" style={{ background: 'radial-gradient(closest-side, color-mix(in srgb, var(--lsp-a) 20%, transparent), transparent 72%)' }} />

            {etape === 'ENROLEMENT' && (
                <div className="fixed inset-0 z-10 flex flex-col items-center justify-center gap-7 p-8 text-center">
                    <MonitorX size={64} strokeWidth={1.4} className="text-white/70" />
                    <h1 className="text-4xl font-bold tracking-tight text-white">Écran non enregistré</h1>
                    {pairCode ? (
                        <>
                            <div className="flex items-center gap-8">
                                <CodeQR value={pairCode} />
                                <div className="rounded-2xl border-4 border-white/25 bg-white/10 px-10 py-6">
                                    <div className="font-mono text-8xl font-black tracking-[0.35em] pl-4 text-white">{pairCode}</div>
                                </div>
                            </div>
                            <p className="text-xl text-white/80">À saisir dans le back office : <b>Paramétrages › Bornes et écrans › Écrans</b></p>
                            {pairExpire && (
                                <p className="text-sm text-white/50">Code valable jusqu'à {pairExpire.toLocaleTimeString('fr-FR')}</p>
                            )}
                        </>
                    ) : (
                        <div className="flex items-center gap-3 text-white/70 text-xl">
                            <Loader2 size={26} className="animate-spin" />
                            {erreurPairing || 'Génération du code d\'enrôlement…'}
                        </div>
                    )}
                </div>
            )}

            {etape === 'CONNEXION' && (
                <div className="eb-livescreem-loading">
                    <img width={300} src={Logo} alt="" />
                    <div className="eb-livescreem-loading-text">
                        <Loader2 size={32} className="animate-spin" style={{ color: 'rgb(25, 7, 143)' }} />
                        <label>Connexion au serveur…</label>
                    </div>
                </div>
            )}

            {etape === 'AFFICHAGE' && (
                <>
                    {/* Barre flottante : marque + direct + horloge */}
                    <header className="eb-lsp-bar">
                        <div className="eb-lsp-bar-brand">
                            {aff.LOGO && <img src={aff.LOGO} alt="" className="eb-lsp-bar-logo" />}
                            <div className="eb-lsp-bar-text">
                                {aff.LT1 && <span className="eb-lsp-bar-t1">{aff.LT1}</span>}
                                <span className="eb-lsp-bar-name">{fileData?.Ecran?.LibelleFiliale || fileData?.Ecran?.Libelle || 'eBCall'}</span>
                            </div>
                        </div>
                        <div className="eb-lsp-bar-right">
                            {/* Ecran.VoixActive (information) : voix coupée depuis le back office */}
                            {(fileData?.Ecran?.VoixActive === false) && (
                                <span className="eb-lsp-live" title="Voix coupée pour cet écran">
                                    <VolumeX size={16} />
                                </span>
                            )}
                            <span className={`eb-lsp-live is-${hubEtat}`}>
                                <i />
                                {hubEtat === 'connected' ? 'EN DIRECT'
                                    : hubEtat === 'reconnecting' ? 'RECONNEXION…'
                                        : 'HORS LIGNE'}
                            </span>
                            <div className="eb-lsp-clock">
                                <span className="eb-lsp-clock-time">{heure}</span>
                                <span className="eb-lsp-clock-date">{new Date().toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                            </div>
                        </div>
                    </header>

                    {/* Corps — mêmes layouts que LiveScreenPro */}
                    <main className="eb-lsp-main">
                        {layout === 'split' && (
                            <>
                                {leftPanel}
                                {rightPanel}
                            </>
                        )}
                        {layout === 'board' && boardPanel}
                        {layout === 'pub' && pubPanel}
                    </main>

                    {/* Ticker bas */}
                    {aff.LT2 && (
                        <footer className="eb-lsp-ticker">
                            <span className="eb-lsp-ticker-tag">INFO</span>
                            <div className="marquee-rtl"><div>{aff.LT2}</div></div>
                        </footer>
                    )}

                    {/* Contrôles (survol haut) */}
                    <div className={`eb-livescreem-controls${showControls ? ' is-visible' : ''}`}>
                        <IconButton tooltip="Vue partagée" size={36}
                            color={layout === 'split' ? 'primary' : 'default'}
                            onClick={() => setLayout('split')}>
                            <LayoutGrid size={18} />
                        </IconButton>
                        <IconButton tooltip="Tableau des appels en plein écran" size={36}
                            color={layout === 'board' ? 'primary' : 'default'}
                            onClick={() => setLayout('board')}>
                            <Rows size={18} />
                        </IconButton>
                        <IconButton tooltip="Publicités en plein écran" size={36}
                            color={layout === 'pub' ? 'primary' : 'default'}
                            onClick={() => setLayout('pub')}>
                            <PanelRight size={18} />
                        </IconButton>
                        <div className="eb-livescreem-controls-divider" />
                        <IconButton tooltip="Inverser les positions" size={36}
                            onClick={() => setSwapped(!swapped)}
                            disabled={layout !== 'split'}>
                            <ArrowLeftRight size={18} />
                        </IconButton>
                        <div className="eb-livescreem-controls-divider" />
                        <IconButton tooltip="Personnaliser les couleurs" size={36}
                            color={paletteOpen ? 'primary' : 'default'}
                            onClick={() => setPaletteOpen(!paletteOpen)}>
                            <Palette size={18} />
                        </IconButton>
                    </div>

                    {/* Palette de personnalisation des couleurs */}
                    {paletteOpen && (
                        <div className="eb-lsp-palette" onMouseDown={(e) => e.stopPropagation()}>
                            <div className="eb-lsp-palette-head">
                                <Palette size={14} />
                                <span>Couleurs de l'écran</span>
                                <button type="button" className="eb-lsp-palette-close" title="Fermer"
                                    onClick={() => setPaletteOpen(false)}>
                                    <X size={14} />
                                </button>
                            </div>
                            {/* Thème clair / sombre */}
                            <div className="eb-lsp-mode">
                                <button type="button"
                                    className={`eb-lsp-mode-btn${mode === 'light' ? ' active' : ''}`}
                                    onClick={() => applyMode('light')}>
                                    <Sun size={14} /> Clair
                                </button>
                                <button type="button"
                                    className={`eb-lsp-mode-btn${mode === 'dark' ? ' active' : ''}`}
                                    onClick={() => applyMode('dark')}>
                                    <Moon size={14} /> Sombre
                                </button>
                            </div>
                            <div className="eb-lsp-palette-presets">
                                {PRESETS.map(([a, b]) => (
                                    <button
                                        key={a + b}
                                        type="button"
                                        className="eb-lsp-preset"
                                        style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
                                        title={`${a} / ${b}`}
                                        onClick={() => applyColors(a, b)}
                                    />
                                ))}
                            </div>
                            <div className="eb-lsp-palette-pickers">
                                <label>
                                    Accent
                                    <input type="color" value={accent} onChange={(e) => applyColors(e.target.value, accB)} />
                                </label>
                                <label>
                                    Halo
                                    <input type="color" value={accent2} onChange={(e) => applyColors(accA, e.target.value)} />
                                </label>
                            </div>
                            <button className="eb-lsp-palette-reset" type="button" onClick={() => applyColors(null, null)}>
                                <RotateCcw size={13} /> Couleurs du tenant
                            </button>
                        </div>
                    )}

                    {/* ===== Appel en PLEIN ÉCRAN ===== */}
                    {featured && (
                        <div className={`ebc-call-overlay${callOut ? ' is-out' : ''}`} key={featured.numero}>
                            <div className="ebc-call-badge"><Megaphone size={26} /> APPEL</div>
                            <div className="ebc-call-num" id="ebc-call-num">{featured.numero}</div>
                            <div className="ebc-call-dest">
                                {featured.box ? `${services.find((s) => s.CodeS === featured.codeS)?.Libelle || ''} ${featured.box}` : (services.find((s) => s.CodeS === featured.codeS)?.Libelle || '')}
                            </div>
                        
                            {featured.msg && featured.msg !== featured.numero && (
                                <div className="ebc-call-msg" key={featured.msg}>{featured.msg}</div>
                            )}
                        </div>
                    )}

                    {/* ===== Vol du ticket vers sa carte (fin d'appel) ===== */}
                    {fly && (
                        <TicketVolant fly={fly} onDone={() => {
                            const n = fly.numero
                            setFly(null)
                            setLanded(n)                                   // flash de réception sur la carte
                            timers.current.feat = setTimeout(() => setLanded(null), 2000)
                        }} />
                    )}
                </>
            )}
        </div>
    );
}
